import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { AuthState, AuthStore, RemoteConfigStore } from '@store';
import { ViToastService } from '@vi-iot/ui';
import { Observable } from 'rxjs';
import { filter, map, switchMap, withLatestFrom } from 'rxjs/operators';
import {
  restrictedTestAutomationInstallations,
  TestAutomationInstallationsListItem,
} from '../common/restricted-test-automation-installations';

@Injectable({
  providedIn: 'root',
})
export class TestAutomationInstallationAccessGuardService {
  constructor(
    private remoteConfigStore: RemoteConfigStore,
    private authStore: AuthStore,
    private viToastService: ViToastService,
    private translate: TranslateService,
    private router: Router
  ) {}

  canActivate(route: ActivatedRouteSnapshot): Observable<boolean> {
    const installationId = +route.params.id;

    return this.canOpenInstallationDetailsPage(installationId);
  }

  private canOpenInstallationDetailsPage(installationId: number): Observable<boolean> {
    return this.authStore.state$.pipe(
      filter((state: AuthState) => {
        if (!state.authorized && !state.authorizing && !state.authenticating) {
          this.router.navigate(['/home']);
        }

        return state.authorized;
      }),
      switchMap(() => this.authStore.userId$),
      withLatestFrom(
        this.remoteConfigStore.isRestrictedAccessForTestAutomationInstallationsEnabled$
      ),
      map(([userId, isRestrictedAccessForTestAutomationInstallationsEnabled]) =>
        isRestrictedAccessForTestAutomationInstallationsEnabled
          ? this.canShowRestrictedTestAutomationInstallation(installationId, userId)
          : true
      )
    );
  }

  private canShowRestrictedTestAutomationInstallation(
    installationId: number,
    userId: string
  ): boolean {
    return restrictedTestAutomationInstallations.some(
      (testInstallationItem: TestAutomationInstallationsListItem) => {
        const isInstallationOnList = !!testInstallationItem.installationIds.includes(
          installationId
        );
        if (!isInstallationOnList) {
          return true;
        }

        if (isInstallationOnList && !testInstallationItem.accessUsersIds.includes(userId)) {
          this.viToastService.info({
            message: this.translate.instant('+INSTALLATIONS.INSTALLATIONS.QA_PURPOSES_ONLY'),
          });

          return false;
        }

        return true;
      }
    );
  }
}
